import * as React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  Chip,
  LinearProgress,
} from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import axios from "axios";

import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import Battery6BarRoundedIcon from "@mui/icons-material/Battery6BarRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import SouthRoundedIcon from "@mui/icons-material/SouthRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";

import { Colors } from "../../utils/Colors";
import HumanBodyImg from "../../assets/img/body.jpg";

const http = axios.create({
  baseURL: "http://127.0.0.1:8000/api",
  timeout: 15000,
});

const dripFall = keyframes`
  0% { transform: translate(-50%, 0px) scale(0.75); opacity: 0.15; }
  20% { opacity: 1; }
  100% { transform: translate(-50%, 88px) scale(1.02); opacity: 0; }
`;

const urineWave = keyframes`
  0% { transform: scaleY(0.35); opacity: 0.45; }
  50% { transform: scaleY(1); opacity: 1; }
  100% { transform: scaleY(0.35); opacity: 0.45; }
`;

const alarmFlash = keyframes`
  0% { box-shadow: 0 0 0 ${alpha(Colors.status?.danger || "#d32f2f", 0)}; }
  50% { box-shadow: 0 0 14px ${alpha(Colors.status?.danger || "#d32f2f", 0.35)}; }
  100% { box-shadow: 0 0 0 ${alpha(Colors.status?.danger || "#d32f2f", 0)}; }
`;

const bodyPulse = keyframes`
  0% { transform: scale(1); opacity: 0.95; }
  50% { transform: scale(1.015); opacity: 1; }
  100% { transform: scale(1); opacity: 0.95; }
`;

const flowDash = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 24px 0; }
`;

const bubbleRise = keyframes`
  0% { transform: translateY(8px); opacity: 0.25; }
  50% { opacity: 1; }
  100% { transform: translateY(-10px); opacity: 0; }
`;

const glowPulse = keyframes`
  0% { opacity: 0.5; transform: translateX(-50%) scale(0.96); }
  50% { opacity: 1; transform: translateX(-50%) scale(1.04); }
  100% { opacity: 0.5; transform: translateX(-50%) scale(0.96); }
`;

const glowPulseCenter = keyframes`
  0% { opacity: 0.5; transform: scale(0.96); }
  50% { opacity: 1; transform: scale(1.04); }
  100% { opacity: 0.5; transform: scale(0.96); }
`;

function fmt(v) {
  if (v === undefined || v === null || v === "") return "—";
  return String(v);
}

function toArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function pick(obj, keys, fallback = "") {
  if (!obj) return fallback;
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
}

function normalizeKey(v) {
  return String(v ?? "").trim().toLowerCase();
}

function getAssignmentId(row) {
  return pick(row, ["device_assignment_id", "assignment_id", "id"]);
}

function getDeviceSerial(row) {
  return pick(row, [
    "device_serial_number",
    "device_serial",
    "serial_number",
    "device_number",
    "device_code",
    "device_id",
    "device",
  ]);
}

function getAssignmentAdmissionNumber(row) {
  return pick(row, [
    "admission_number",
    "admission_no",
    "admission_id",
    "admission",
    "admission_ref",
    "admission_code",
  ]);
}

function getAdmissionNumber(row) {
  return pick(row, [
    "admission_number",
    "admission_no",
    "admission_id",
    "admission",
    "admission_ref",
    "admission_code",
  ]);
}

function getAdmissionId(row) {
  return pick(row, ["id", "admission_uuid", "uuid", "admission_id"]);
}

function getPatientName(adm) {
  return pick(adm, ["patient_name", "full_name", "name", "patient_full_name"]);
}

function getAdmissionUhid(adm) {
  return pick(adm, [
    "patient_uhid_out",
    "uhid",
    "uid",
    "patient_uid",
    "patient_uhid",
    "uhid_number",
  ]);
}

function getPatientPossibleUhids(patient) {
  return [
    patient?.uhid,
    patient?.patient_uhid_out,
    patient?.patient_uhid,
    patient?.uhid_number,
    patient?.uid,
  ]
    .filter((x) => x !== undefined && x !== null && x !== "")
    .map((x) => String(x).trim());
}

function getAge(patient, admission) {
  return pick(
    patient,
    ["age", "patient_age", "current_age", "age_years"],
    pick(admission, ["age", "patient_age"])
  );
}

function getSex(patient, admission) {
  return pick(
    patient,
    ["sex", "gender", "patient_gender"],
    pick(admission, ["sex", "gender", "patient_gender"])
  );
}

function getDoctor(adm) {
  return pick(adm, [
    "treating_doctor",
    "consultant",
    "doctor_name",
    "consultant_name",
    "doctor",
    "doctor_assigned",
    "attending_doctor",
  ]);
}

function getWard(assignment, admission) {
  return (
    pick(assignment, ["ward_name", "ward", "ward_label", "ward_unit"]) ||
    pick(admission, ["ward_unit", "ward_name", "ward"])
  );
}

function getBed(assignment, admission) {
  return (
    pick(assignment, ["bed_number", "bed_no", "bed", "bed_label", "room_label"]) ||
    pick(admission, ["bed_number", "bed_no", "bed", "room_label"])
  );
}

function getHospitalName(admission, hospitalMap) {
  const hospitalId = pick(admission, [
    "hospital_id",
    "hospital",
    "organization",
    "organization_id",
  ]);
  if (!hospitalId) return "";
  return hospitalMap.get(String(hospitalId)) || String(hospitalId);
}

function normalizeFluidTherapy(row) {
  if (!row) return null;

  return {
    id: row.id,
    admission: pick(row, ["admission", "admission_id"]),
    admission_number: pick(row, ["admission_number", "admission_no"]),
    patient_uhid: pick(row, ["patient_uhid", "patient_uhid_out", "uhid"]),
    patient_name: pick(row, ["patient_name"]),
    doctor_name: pick(row, ["doctor_name"]),
    fluid: pick(row, ["fluid"]),
    fluid_label: pick(row, ["fluid_label"]),
    qty_ml: pick(row, ["qty_ml"]),
    qty_label: pick(row, ["qty_label"]),
    speed_ml_hr: pick(row, ["speed_ml_hr"]),
    frequency: pick(row, ["frequency"]),
    duration: pick(row, ["duration"]),
    created_at: pick(row, ["created_at"]),
  };
}

function buildMergedRow(assignment, admission, patient, hospitalMap, index, fluidTherapy) {
  const safeAdmission = admission || {};
  const safePatient = patient || {};

  const liveFlowRate = [75, 72, 84, 67, 79, 70][index % 6];
  const fluidIn = [500, 420, 610, 350, 720, 460][index % 6];
  const fluidOut = [280, 180, 390, 150, 470, 210][index % 6];
  const battery = [67, 81, 74, 59, 90, 63][index % 6];
  const alarm = ["FLOW SLOW", "NORMAL", "NORMAL", "CHECK LINE", "NORMAL", "LOW BOTTLE"][index % 6];

  const prescribedSpeed = Number(fluidTherapy?.speed_ml_hr || 0);
  const bottleMl = Number(fluidTherapy?.qty_ml || 0);

  return {
    id: getAssignmentId(assignment) || `${getDeviceSerial(assignment)}-${index}`,
    deviceSerial: getDeviceSerial(assignment),
    admissionId: getAdmissionId(safeAdmission),
    admissionNumber:
      getAssignmentAdmissionNumber(assignment) || getAdmissionNumber(safeAdmission),

    patientName: getPatientName(safeAdmission),
    uhid: getAdmissionUhid(safeAdmission),
    age: getAge(safePatient, safeAdmission),
    sex: getSex(safePatient, safeAdmission),
    doctorName: getDoctor(safeAdmission),
    hospitalName: getHospitalName(safeAdmission, hospitalMap),
    wardName: getWard(assignment, safeAdmission),
    bedNumber: getBed(assignment, safeAdmission),

    assignedAt: pick(assignment, ["assigned_at", "created_at", "updated_at"]),
    status: "RUNNING",
    battery,
    network: "ONLINE",

    flowRate: liveFlowRate,
    prescribedSpeed,
    bottleMl,
    bottleLabel: fluidTherapy?.qty_label || (bottleMl ? `${bottleMl} ml` : "—"),

    totalVolume: bottleMl || 1000,
    fluidIn,
    fluidOut,
    netBalance: (Number(fluidIn) || 0) - (Number(fluidOut) || 0),
    eta: ["02:00 hr", "01:20 hr", "00:55 hr", "02:40 hr", "00:35 hr", "01:45 hr"][index % 6],
    alarm,
    lastUpdate: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),

    fluidTherapy: fluidTherapy || null,
  };
}

async function getMappedRows() {
  const [assignmentRes, admissionRes, patientRes, hospitalRes, therapyRes] = await Promise.all([
    http.get("/devices/device-assignments/?format=json"),
    http.get("/patients/admissions/?format=json"),
    http.get("/patients/patients/?format=json"),
    http.get("/hospitals/?format=json"),
    http.get("/medcharts/fluid-therapy/?format=json"),
  ]);

  const assignments = toArray(assignmentRes.data);
  const admissions = toArray(admissionRes.data);
  const patients = toArray(patientRes.data);
  const hospitals = toArray(hospitalRes.data);
  const fluidTherapies = toArray(therapyRes.data).map(normalizeFluidTherapy);

  const admissionMap = new Map();
  admissions.forEach((adm) => {
    const byNumber = normalizeKey(getAdmissionNumber(adm));
    if (byNumber) admissionMap.set(byNumber, adm);
  });

  const patientMap = new Map();
  patients.forEach((patient) => {
    getPatientPossibleUhids(patient).forEach((uhid) => {
      patientMap.set(normalizeKey(uhid), patient);
    });
  });

  const hospitalMap = new Map();
  hospitals.forEach((hospital) => {
    const id = pick(hospital, ["hospital_id", "id"]);
    const name = pick(hospital, ["name", "hospital_name"]);
    if (id && name) hospitalMap.set(String(id), name);
  });

  return assignments.map((assignment, index) => {
    const assignmentAdmissionNumber = normalizeKey(getAssignmentAdmissionNumber(assignment));
    const admission = admissionMap.get(assignmentAdmissionNumber) || null;

    const patientUhid = normalizeKey(getAdmissionUhid(admission || {}));
    const admissionNumber = normalizeKey(getAdmissionNumber(admission || {}));
    const patient = patientMap.get(patientUhid) || null;

    const matchedTherapy =
      fluidTherapies.find(
        (therapy) => normalizeKey(therapy.patient_uhid) === patientUhid && patientUhid
      ) ||
      fluidTherapies.find(
        (therapy) => normalizeKey(therapy.admission_number) === admissionNumber && admissionNumber
      ) ||
      null;

    return buildMergedRow(
      assignment,
      admission,
      patient,
      hospitalMap,
      index,
      matchedTherapy
    );
  });
}

function statusBg(status) {
  if (status === "RUNNING") return Colors.status.success;
  if (status === "ON_HOLD") return Colors.status.warning;
  if (status === "COMPLETED") return Colors.status.info;
  return Colors.text.sub;
}

function Panel({ title, children, right, sx = {} }) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        color: Colors.text.body,
        background: Colors.bg.paper,
        border: `1px solid ${Colors.border.light}`,
        boxShadow: Colors.shadow.brand,
        height: "100%",
        ...sx,
      }}
    >
      <CardContent sx={{ p: { xs: 1.3, md: 1.6 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 900, color: Colors.text.title }}>
            {title}
          </Typography>
          {right}
        </Stack>
        <Divider sx={{ borderColor: Colors.border.light, mb: 1.1 }} />
        {children}
      </CardContent>
    </Card>
  );
}

function DetailLine({ label, value }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{ py: 0.42, borderBottom: `1px solid ${Colors.border.topLine}` }}
    >
      <Typography sx={{ width: 82, minWidth: 82, fontSize: 11, fontWeight: 800, color: Colors.text.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: Colors.text.body,
          wordBreak: "break-word",
          lineHeight: 1.25,
        }}
      >
        {fmt(value)}
      </Typography>
    </Stack>
  );
}

function InfoLabel({ label, value, big = false }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: Colors.text.muted, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.2,
          fontSize: big ? { xs: 18, md: 22 } : { xs: 14, md: 16 },
          fontWeight: 900,
          color: Colors.text.body,
          lineHeight: 1.1,
          wordBreak: "break-word",
        }}
      >
        {fmt(value)}
      </Typography>
    </Box>
  );
}

function AnimatedDripBottle({ speed = 75, active = false }) {
  const duration = Math.max(0.6, 2.6 - speed / 65);

  return (
    <Box sx={{ position: "relative", width: 96, height: 138, mx: "auto", opacity: active ? 1 : 0.55 }}>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 16,
          height: 16,
          borderRadius: "6px 6px 0 0",
          border: `2px solid ${Colors.brand.primary}`,
          borderBottom: "none",
          bgcolor: Colors.bg.paper,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 12,
          left: "50%",
          transform: "translateX(-50%)",
          width: 62,
          height: 68,
          borderRadius: "14px 14px 16px 16px",
          border: `2px solid ${Colors.brand.primary}`,
          bgcolor: alpha(Colors.brand.primary, 0.05),
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "58%",
            bgcolor: alpha(Colors.status.success, 0.22),
            borderTop: `2px solid ${Colors.status.success}`,
          }}
        />
      </Box>
      <Box
        sx={{
          position: "absolute",
          top: 82,
          left: "50%",
          transform: "translateX(-50%)",
          width: 20,
          height: 24,
          borderRadius: "9px 9px 11px 11px",
          border: `2px solid ${Colors.brand.primary}`,
          bgcolor: alpha(Colors.brand.primary, 0.04),
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 104,
          left: "50%",
          transform: "translateX(-50%)",
          width: 3,
          height: 26,
          bgcolor: alpha(Colors.brand.primary, active ? 0.75 : 0.25),
          borderRadius: 999,
        }}
      />
      {[0, 0.5, 1].map((delay, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: 95,
            left: "50%",
            width: 8,
            height: 12,
            borderRadius: "50% 50% 55% 55%",
            bgcolor: Colors.brand.primary,
            animation: active ? `${dripFall} ${duration}s linear ${delay}s infinite` : "none",
            opacity: active ? 1 : 0,
          }}
        />
      ))}
      <Typography
        sx={{
          position: "absolute",
          bottom: -4,
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: 11,
          fontWeight: 800,
          color: active ? Colors.status.success : Colors.text.muted,
        }}
      >
        {active ? "Drip Running" : "Drip Stopped"}
      </Typography>
    </Box>
  );
}

function AnimatedUrineBag({ value = 0, active = false }) {
  const fillPercent = Math.max(12, Math.min(85, Math.round((Number(value) || 0) / 6)));
  const urineYellow = "#D9B63D";

  return (
    <Box sx={{ position: "relative", width: 110, height: 150, mx: "auto", opacity: active ? 1 : 0.7 }}>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 22,
          height: 18,
          border: `2px solid ${Colors.brand.primary}`,
          borderBottom: "none",
          borderRadius: "10px 10px 0 0",
          bgcolor: Colors.bg.paper,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 16,
          left: "50%",
          transform: "translateX(-50%)",
          width: 78,
          height: 110,
          border: `2px solid ${Colors.brand.primary}`,
          borderRadius: "16px 16px 18px 18px",
          overflow: "hidden",
          bgcolor: alpha(Colors.brand.primary, 0.04),
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: `${fillPercent}%`,
            bgcolor: alpha(urineYellow, 0.46),
            borderTop: `2px solid ${urineYellow}`,
            animation: active ? `${urineWave} 1.6s ease-in-out infinite` : "none",
            transformOrigin: "bottom",
          }}
        />
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ position: "absolute", inset: 0, textAlign: "center", px: 1 }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 900, color: Colors.text.title }}>
            {fmt(value)}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: Colors.text.body }}>
            ml
          </Typography>
        </Stack>
      </Box>
      <Typography
        sx={{
          position: "absolute",
          bottom: -4,
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: 11,
          fontWeight: 800,
          color: active ? urineYellow : Colors.text.muted,
        }}
      >
        {active ? "Output Updating" : "Output Stable"}
      </Typography>
    </Box>
  );
}

function AlarmPanel({ alarm }) {
  const danger = alarm && alarm !== "NORMAL";

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: `1px solid ${Colors.border.light}`,
        background: danger ? alpha(Colors.status.danger, 0.08) : Colors.bg.paper,
        boxShadow: Colors.shadow.brand,
        height: "100%",
        animation: danger ? `${alarmFlash} 1.3s infinite` : "none",
      }}
    >
      <CardContent sx={{ p: { xs: 1.3, md: 1.6 } }}>
        <Stack spacing={1}>
          <Typography sx={{ fontSize: 15, fontWeight: 900, color: Colors.text.title }}>
            Alarm Panel
          </Typography>
          <Divider sx={{ borderColor: Colors.border.light }} />
          <Chip
            icon={danger ? <WarningAmberRoundedIcon sx={{ color: `${Colors.bg.paper} !important` }} /> : undefined}
            label={alarm || "NORMAL"}
            sx={{
              bgcolor: danger ? Colors.status.danger : Colors.status.success,
              color: Colors.bg.paper,
              fontWeight: 900,
              width: "fit-content",
            }}
          />
          <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
            {danger ? "Attention required. Check IV line or flow." : "No active alarm."}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function TherapyValueCard({ label, value }) {
  return (
    <Box
      sx={{
        p: 1.15,
        borderRadius: 2,
        border: `1px solid ${Colors.border.light}`,
        bgcolor: alpha(Colors.brand.primary, 0.03),
        minHeight: 74,
      }}
    >
      <Typography sx={{ fontSize: 11, color: Colors.text.muted, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.55,
          fontSize: 15,
          fontWeight: 900,
          color: Colors.text.title,
          lineHeight: 1.2,
          wordBreak: "break-word",
        }}
      >
        {fmt(value)}
      </Typography>
    </Box>
  );
}

function FluidTherapyInfoCard({ row }) {
  const therapy = row?.fluidTherapy;

  return (
    <Panel
      title="Fluid Therapy Information"
      right={<MedicationOutlinedIcon sx={{ color: Colors.text.icon }} />}
      sx={{ background: alpha(Colors.brand.primary, 0.04) }}
    >
      {!therapy ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No fluid therapy information found for this patient.
        </Alert>
      ) : (
        <Grid container spacing={1.2}>
          <Grid item xs={12} sm={6} md={3}>
            <TherapyValueCard label="Patient Name" value={therapy.patient_name} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TherapyValueCard label="Doctor Name" value={therapy.doctor_name} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TherapyValueCard label="Fluid Code" value={therapy.fluid} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TherapyValueCard label="Fluid Name" value={therapy.fluid_label} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TherapyValueCard label="Bottle Size" value={therapy.qty_label || `${therapy.qty_ml} ml`} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TherapyValueCard label="Bottle ml" value={therapy.qty_ml} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TherapyValueCard label="Speed ml/hr" value={therapy.speed_ml_hr} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TherapyValueCard label="Frequency" value={therapy.frequency} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TherapyValueCard label="Duration" value={therapy.duration} />
          </Grid>
        </Grid>
      )}
    </Panel>
  );
}

function HumanFluidBalanceChart({ fluidIn = 0, fluidOut = 0, netBalance = 0 }) {
  const safeIn = Number(fluidIn) || 0;
  const safeOut = Number(fluidOut) || 0;
  const safeNet = Number(netBalance) || 0;
  const positive = safeNet >= 0;

  const intakeWidth = Math.max(12, Math.min(100, Math.round(safeIn / 10)));
  const outputWidth = Math.max(12, Math.min(100, Math.round(safeOut / 10)));

  const urineYellow = "#D9B63D";
  const urineYellowSoft = alpha(urineYellow, 0.16);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: { xs: 320, md: 340 },
        borderRadius: 3,
        border: `1px solid ${Colors.border.light}`,
        bgcolor: "#F7F8F8",
        overflow: "hidden",
        px: { xs: 1, md: 1.4 },
        py: { xs: 1.2, md: 1.5 },
      }}
    >
      <Grid container spacing={1.2} alignItems="center">
        <Grid item xs={12} md={3}>
          <Stack spacing={1} alignItems={{ xs: "center", md: "flex-start" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: Colors.text.title }}>
              Fluid In
            </Typography>

            <Box
              sx={{
                width: "100%",
                maxWidth: 160,
                p: 1,
                borderRadius: 2,
                border: `1px solid ${alpha(Colors.status.success, 0.25)}`,
                bgcolor: alpha(Colors.status.success, 0.14),
                boxShadow: `0 8px 20px ${alpha(Colors.status.success, 0.08)}`,
              }}
            >
              <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 700 }}>
                Input
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: Colors.text.body }}>
                {fmt(safeIn)} ml
              </Typography>
            </Box>

            <Box
              sx={{
                width: "100%",
                maxWidth: 160,
                height: 9,
                borderRadius: 999,
                bgcolor: alpha(Colors.status.success, 0.12),
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${intakeWidth}%`,
                  height: "100%",
                  bgcolor: Colors.status.success,
                  borderRadius: 999,
                  transition: "width 0.5s ease",
                }}
              />
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: { xs: 290, md: 310 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: { xs: "4%", md: "2%" },
                top: { xs: "28%", md: "29%" },
                width: { xs: "28%", md: "30%" },
                height: 4,
                backgroundImage: `linear-gradient(90deg, ${Colors.status.success} 50%, transparent 50%)`,
                backgroundSize: "14px 4px",
                backgroundRepeat: "repeat-x",
                animation: `${flowDash} 1.1s linear infinite`,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  right: -2,
                  top: -4,
                  borderTop: "6px solid transparent",
                  borderBottom: "6px solid transparent",
                  borderLeft: `10px solid ${Colors.status.success}`,
                },
              }}
            />

            <Box
              sx={{
                position: "absolute",
                right: { xs: "4%", md: "2%" },
                top: { xs: "52%", md: "53%" },
                width: { xs: "28%", md: "30%" },
                height: 4,
                backgroundImage: `linear-gradient(90deg, ${urineYellow} 50%, transparent 50%)`,
                backgroundSize: "14px 4px",
                backgroundRepeat: "repeat-x",
                animation: `${flowDash} 1.1s linear infinite`,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: -2,
                  top: -4,
                  borderTop: "6px solid transparent",
                  borderBottom: "6px solid transparent",
                  borderRight: `10px solid ${urineYellow}`,
                },
              }}
            />

            <Box
              sx={{
                position: "relative",
                width: { xs: 150, sm: 170, md: 185 },
                height: { xs: 240, sm: 265, md: 285 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: `${bodyPulse} 2.6s ease-in-out infinite`,
              }}
            >
              <Box
                component="img"
                src={HumanBodyImg}
                alt="Human Body"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                  userSelect: "none",
                  pointerEvents: "none",
                  filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.08))",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: { xs: "24%", md: "23%" },
                  transform: "translateX(-50%)",
                  width: { xs: 34, md: 40 },
                  height: { xs: 72, md: 82 },
                  borderRadius: "50% 50% 45% 45%",
                  bgcolor: alpha(Colors.status.success, 0.25),
                  border: `2px solid ${Colors.status.success}`,
                  boxShadow: `0 0 18px ${alpha(Colors.status.success, 0.3)}`,
                  animation: `${glowPulse} 2s ease-in-out infinite`,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "58%",
                    height: "58%",
                    borderRadius: "50% 50% 55% 55%",
                    border: `1.5px solid ${alpha(Colors.status.success, 0.95)}`,
                    bgcolor: alpha(Colors.status.success, 0.1),
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: "20%",
                    bottom: "18%",
                    width: 1.5,
                    transform: "translateX(-50%)",
                    bgcolor: alpha(Colors.status.success, 0.45),
                    borderRadius: 999,
                    borderLeft: `1px dashed ${alpha(Colors.status.success, 0.55)}`,
                  }}
                />
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: { xs: "55%", md: "56%" },
                  transform: "translateX(-50%)",
                  width: { xs: 24, md: 28 },
                  height: { xs: 24, md: 28 },
                  borderRadius: "50%",
                  bgcolor: alpha(urineYellow, 0.3),
                  border: `2px solid ${urineYellow}`,
                  boxShadow: `0 0 14px ${alpha(urineYellow, 0.26)}`,
                  animation: `${glowPulseCenter} 1.8s ease-in-out infinite`,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: "42%",
                    height: "52%",
                    transform: "translate(-50%, -50%)",
                    borderRadius: "50% 50% 60% 60%",
                    border: `1.2px solid ${alpha(urineYellow, 0.95)}`,
                    bgcolor: alpha(urineYellow, 0.1),
                  }}
                />
              </Box>

              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={`green-dot-${i}`}
                  sx={{
                    position: "absolute",
                    left: `calc(50% - ${28 - i * 7}px)`,
                    top: { xs: `${29 - i * 1.4}%`, md: `${28 - i * 1.4}%` },
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: Colors.status.success,
                    boxShadow: `0 0 8px ${alpha(Colors.status.success, 0.4)}`,
                    animation: `${bubbleRise} ${1.2 + i * 0.18}s ease-in-out infinite`,
                  }}
                />
              ))}

              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={`yellow-dot-${i}`}
                  sx={{
                    position: "absolute",
                    left: `calc(50% + ${18 + i * 7}px)`,
                    top: { xs: `${57 - i * 1.3}%`, md: `${57 - i * 1.3}%` },
                    width: 5.5,
                    height: 5.5,
                    borderRadius: "50%",
                    bgcolor: urineYellow,
                    boxShadow: `0 0 8px ${alpha(urineYellow, 0.35)}`,
                    animation: `${bubbleRise} ${1.2 + i * 0.18}s ease-in-out infinite`,
                  }}
                />
              ))}
            </Box>

            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                px: 1.6,
                py: 0.55,
                borderRadius: 999,
                bgcolor: positive
                  ? alpha(Colors.status.success, 0.14)
                  : alpha(Colors.status.danger, 0.14),
                border: `1px solid ${
                  positive
                    ? alpha(Colors.status.success, 0.28)
                    : alpha(Colors.status.danger, 0.28)
                }`,
                boxShadow: positive
                  ? `0 8px 18px ${alpha(Colors.status.success, 0.12)}`
                  : `0 8px 18px ${alpha(Colors.status.danger, 0.12)}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 900,
                  color: positive ? Colors.status.success : Colors.status.danger,
                }}
              >
                Net {positive ? "+" : ""}
                {fmt(safeNet)} ml
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Stack spacing={1} alignItems={{ xs: "center", md: "flex-end" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: Colors.text.title }}>
              Fluid Out
            </Typography>

            <Box
              sx={{
                width: "100%",
                maxWidth: 160,
                p: 1,
                borderRadius: 2,
                border: `1px solid ${alpha(urineYellow, 0.3)}`,
                bgcolor: urineYellowSoft,
                boxShadow: `0 8px 20px ${alpha(urineYellow, 0.08)}`,
              }}
            >
              <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 700 }}>
                Output
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: Colors.text.body }}>
                {fmt(safeOut)} ml
              </Typography>
            </Box>

            <Box
              sx={{
                width: "100%",
                maxWidth: 160,
                height: 9,
                borderRadius: 999,
                bgcolor: alpha(urineYellow, 0.16),
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${outputWidth}%`,
                  height: "100%",
                  bgcolor: urineYellow,
                  borderRadius: 999,
                  transition: "width 0.5s ease",
                }}
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

export default function FluidMonitoringPage() {
  const [rows, setRows] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [animationState, setAnimationState] = React.useState({});
  const prevRowsRef = React.useRef({});

  const loadData = React.useCallback(async () => {
    try {
      setError("");
      const data = await getMappedRows();
      setRows(data);

      const nextAnimationState = {};
      const nextPrevRows = { ...prevRowsRef.current };

      data.forEach((row) => {
        const oldRow = prevRowsRef.current[row.id];

        const fluidOutChanged =
          oldRow && Number(row.fluidOut || 0) > Number(oldRow.fluidOut || 0);

        const dripRunning =
          row.status === "RUNNING" &&
          Number(row.flowRate || 0) > 0 &&
          row.alarm !== "FLOW STOP" &&
          row.alarm !== "NO FLOW";

        const urineRunning = Boolean(fluidOutChanged);

        nextAnimationState[row.id] = {
          dripRunning,
          urineRunning,
        };

        nextPrevRows[row.id] = row;
      });

      prevRowsRef.current = nextPrevRows;
      setAnimationState(nextAnimationState);

      setSelectedId((prevSelectedId) => {
        if (prevSelectedId && !data.find((r) => String(r.id) === String(prevSelectedId))) {
          return "";
        }
        return prevSelectedId;
      });

      setLoading(false);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load fluid monitoring page"
      );
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  React.useEffect(() => {
    const timer = setInterval(() => {
      loadData();
    }, 5000);
    return () => clearInterval(timer);
  }, [loadData]);

  const selectedRow =
    rows.find((r) => String(r.id) === String(selectedId)) || null;

  const selectedAnimation =
    (selectedRow && animationState[selectedRow.id]) || {
      dripRunning: false,
      urineRunning: false,
    };

  const selectedOption =
    rows.find((row) => String(row.id) === String(selectedId)) || null;

  return (
    <Box sx={{ minHeight: "100vh", p: { xs: 1, md: 1.5 }, background: Colors.bg.app }}>
      <Stack spacing={1.2}>
        <Card
          sx={{
            borderRadius: 2,
            p: 0.2,
            color: Colors.text.body,
            background: Colors.bg.paper,
            border: `1px solid ${Colors.border.light}`,
            boxShadow: Colors.shadow.brand,
          }}
        >
          <CardContent sx={{ p: { xs: 1.2, md: 1.5 } }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", lg: "center" }}
              spacing={1.5}
            >
              <Box>
                <Typography sx={{ fontSize: { xs: 20, md: 26 }, fontWeight: 900, color: Colors.text.title }}>
                  FLUID MONITORING
                </Typography>
                <Typography sx={{ fontSize: 13, color: Colors.text.muted, mt: 0.4 }}>
                  IV flow, urine out and fluid balance monitoring
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.2}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ width: { xs: "100%", lg: "auto" } }}
              >
                <Autocomplete
                  fullWidth
                  options={rows}
                  value={selectedOption}
                  onChange={(_, newValue) => {
                    setSelectedId(newValue ? String(newValue.id) : "");
                  }}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id) === String(value?.id)
                  }
                  getOptionLabel={(option) => {
                    if (!option) return "";
                    const patient = option.patientName || "No Patient";
                    const serial = option.deviceSerial || "No Device";
                    const uhid = option.uhid || "No UHID";
                    return `${patient} | ${serial} | ${uhid}`;
                  }}
                  filterOptions={(options, state) => {
                    const search = state.inputValue.trim().toLowerCase();
                    if (!search) return options;

                    return options.filter((option) => {
                      const patient = String(option.patientName || "").toLowerCase();
                      const serial = String(option.deviceSerial || "").toLowerCase();
                      const uhid = String(option.uhid || "").toLowerCase();
                      const bed = String(option.bedNumber || "").toLowerCase();
                      const doctor = String(option.doctorName || "").toLowerCase();
                      const admission = String(option.admissionNumber || "").toLowerCase();

                      return (
                        patient.includes(search) ||
                        serial.includes(search) ||
                        uhid.includes(search) ||
                        bed.includes(search) ||
                        doctor.includes(search) ||
                        admission.includes(search)
                      );
                    });
                  }}
                  noOptionsText="No patient / device found"
                  sx={{
                    minWidth: { xs: "100%", sm: 380 },
                    "& .MuiOutlinedInput-root": {
                      color: Colors.text.body,
                      bgcolor: Colors.bg.paper,
                      borderRadius: 2,
                    },
                    "& .MuiSvgIcon-root": { color: Colors.text.icon },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Search patient name / UHID / device serial"
                      label="Select Patient / Device"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Stack spacing={0.2}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: Colors.text.title }}>
                          {option.patientName || "No Patient"}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: Colors.text.body }}>
                          Device: {option.deviceSerial || "—"} | UHID: {option.uhid || "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: Colors.text.muted }}>
                          Bed: {option.bedNumber || "—"} | Doctor: {option.doctorName || "—"}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                />

                {selectedRow && (
                  <Chip
                    label={selectedRow.status}
                    sx={{
                      bgcolor: statusBg(selectedRow.status),
                      color: Colors.bg.paper,
                      fontWeight: 900,
                    }}
                  />
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {loading ? (
          <Card
            sx={{
              borderRadius: 2,
              background: Colors.bg.paper,
              border: `1px solid ${Colors.border.light}`,
              boxShadow: Colors.shadow.brand,
            }}
          >
            <CardContent sx={{ py: 8, textAlign: "center" }}>
              <CircularProgress sx={{ color: Colors.brand.primary }} />
              <Typography sx={{ mt: 2, color: Colors.text.title, fontWeight: 800 }}>
                Loading fluid monitoring...
              </Typography>
            </CardContent>
          </Card>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !rows.length ? (
          <Alert severity="info">No monitoring data found.</Alert>
        ) : !selectedRow ? (
          <Alert severity="info">Please select a FLUDEE serial number.</Alert>
        ) : (
          <>
            <Card
              sx={{
                borderRadius: 2,
                background: Colors.bg.paper,
                border: `1px solid ${Colors.border.light}`,
                boxShadow: Colors.shadow.brand,
              }}
            >
              <CardContent sx={{ p: { xs: 1.2, md: 1.5 } }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 900, color: Colors.text.title }}>
                      Patient: {fmt(selectedRow.patientName)}
                    </Typography>
                    <Typography sx={{ color: Colors.text.muted, fontSize: 14 }}>
                      | UHID: {fmt(selectedRow.uhid)} | Bed: {fmt(selectedRow.bedNumber)} | Age: {fmt(selectedRow.age)} / {fmt(selectedRow.sex)}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <WifiRoundedIcon sx={{ fontSize: 16, color: Colors.status.success }} />
                      <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
                        {fmt(selectedRow.network)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Battery6BarRoundedIcon sx={{ fontSize: 16, color: Colors.text.icon }} />
                      <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
                        Battery: {fmt(selectedRow.battery)}%
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
                      Last Update: {fmt(selectedRow.lastUpdate)}
                    </Typography>
                    <NotificationsNoneRoundedIcon sx={{ fontSize: 18, color: Colors.text.icon }} />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Grid container spacing={1.2}>
              <Grid item xs={12} xl={3.2}>
                <Panel title="Patient Details" right={<PersonRoundedIcon sx={{ color: Colors.text.icon }} />}>
                  <Stack spacing={0.2}>
                    <DetailLine label="Name" value={selectedRow.patientName} />
                    <DetailLine label="UHID" value={selectedRow.uhid} />
                    <DetailLine label="Age" value={selectedRow.age} />
                    <DetailLine label="Sex" value={selectedRow.sex} />
                    <DetailLine label="Doctor" value={selectedRow.doctorName} />
                    <DetailLine label="Ward" value={selectedRow.wardName} />
                    <DetailLine label="Bed" value={selectedRow.bedNumber} />
                    <DetailLine label="Hospital" value={selectedRow.hospitalName} />
                    <DetailLine label="Admission" value={selectedRow.admissionNumber} />
                  </Stack>
                </Panel>
              </Grid>

              <Grid item xs={12} md={6} xl={2.5}>
                <Panel
                  title="Fluid In"
                  right={<MemoryRoundedIcon sx={{ color: Colors.text.icon }} />}
                  sx={{ background: alpha(Colors.status.success, 0.08) }}
                >
                  <Stack spacing={1} alignItems="center" sx={{ textAlign: "center" }}>
                    <AnimatedDripBottle
                      speed={selectedRow.flowRate}
                      active={selectedAnimation.dripRunning}
                    />

                    <Stack direction="row" spacing={1} alignItems="center">
                      <WaterDropRoundedIcon sx={{ color: Colors.brand.primary }} />
                      <Typography sx={{ fontSize: 15, fontWeight: 900, color: Colors.text.title }}>
                        DRIP ON
                      </Typography>
                    </Stack>

                    <Typography sx={{ fontSize: { xs: 34, md: 46 }, fontWeight: 900, lineHeight: 1, color: Colors.text.title }}>
                      {fmt(selectedRow.flowRate)}
                    </Typography>

                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: Colors.text.body }}>
                      ml/hr
                    </Typography>

                    <Divider sx={{ width: "100%", borderColor: Colors.border.light }} />

                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <InfoLabel
                          label="Speed"
                          value={
                            selectedRow.prescribedSpeed
                              ? `${fmt(selectedRow.prescribedSpeed)} ml/hr`
                              : "—"
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoLabel
                          label="Bottle"
                          value={selectedRow.bottleLabel || "—"}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </Panel>
              </Grid>

              <Grid item xs={12} xl={3.8}>
                <Panel
                  title="Fluid Balance Chart"
                  right={<LocalHospitalRoundedIcon sx={{ color: Colors.text.icon }} />}
                >
                  <HumanFluidBalanceChart
                    fluidIn={selectedRow.fluidIn}
                    fluidOut={selectedRow.fluidOut}
                    netBalance={selectedRow.netBalance}
                  />
                </Panel>
              </Grid>

              <Grid item xs={12} md={6} xl={2.5}>
                <Panel
                  title="Fluid Out"
                  right={<SouthRoundedIcon sx={{ color: Colors.text.icon }} />}
                  sx={{ background: alpha("#D9B63D", 0.14) }}
                >
                  <Stack spacing={1} alignItems="center" sx={{ textAlign: "center" }}>
                    <AnimatedUrineBag
                      value={selectedRow.fluidOut}
                      active={selectedAnimation.urineRunning}
                    />

                    <Typography sx={{ fontSize: 15, fontWeight: 900, color: Colors.text.title }}>
                      OUTPUT
                    </Typography>

                    <Typography sx={{ fontSize: { xs: 34, md: 46 }, fontWeight: 900, lineHeight: 1, color: Colors.text.title }}>
                      {fmt(selectedRow.fluidOut)}
                    </Typography>

                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: Colors.text.body }}>
                      ml
                    </Typography>
                  </Stack>
                </Panel>
              </Grid>

              <Grid item xs={12} lg={7.5}>
                <Panel
                  title="Live Fluid Balance"
                  right={<TrendingUpRoundedIcon sx={{ color: Colors.text.icon }} />}
                >
                  <Stack spacing={1.5}>
                    <Grid container spacing={1.2}>
                      <Grid item xs={4}>
                        <InfoLabel label="Input" value={`${fmt(selectedRow.fluidIn)} ml`} />
                      </Grid>
                      <Grid item xs={4}>
                        <InfoLabel label="Output" value={`${fmt(selectedRow.fluidOut)} ml`} />
                      </Grid>
                      <Grid item xs={4}>
                        <InfoLabel
                          label="Net"
                          value={`${selectedRow.netBalance > 0 ? "+" : ""}${fmt(selectedRow.netBalance)} ml`}
                        />
                      </Grid>
                    </Grid>

                    <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 700 }}>
                      Balance Progress
                    </Typography>

                    <LinearProgress
                      variant="determinate"
                      value={Math.max(
                        5,
                        Math.min(
                          100,
                          ((Number(selectedRow.fluidIn) || 0) /
                            Math.max(Number(selectedRow.totalVolume) || 1, 1)) *
                            100
                        )
                      )}
                      sx={{
                        height: 12,
                        borderRadius: 999,
                        bgcolor: alpha(Colors.brand.primary, 0.12),
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 999,
                          background: Colors.brand.primary,
                        },
                      }}
                    />
                  </Stack>
                </Panel>
              </Grid>

              <Grid item xs={12} lg={4.5}>
                <AlarmPanel alarm={selectedRow.alarm} />
              </Grid>

              <Grid item xs={12}>
                <Panel title="Session Overview">
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} md={3}>
                      <InfoLabel label="Admission No" value={selectedRow.admissionNumber} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <InfoLabel label="Device Serial" value={selectedRow.deviceSerial} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <InfoLabel label="Bed" value={selectedRow.bedNumber} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <InfoLabel label="Assigned At" value={selectedRow.assignedAt} />
                    </Grid>
                  </Grid>
                </Panel>
              </Grid>

              <Grid item xs={12}>
                <FluidTherapyInfoCard row={selectedRow} />
              </Grid>
            </Grid>
          </>
        )}
      </Stack>
    </Box>
  );
}



// src/app/monitoring/pages/FluidMonitoringPage.jsx

// import * as React from "react";
// import {
//   Alert,
//   Autocomplete,
//   Box,
//   Card,
//   CardContent,
//   CircularProgress,
//   Divider,
//   Grid,
//   Stack,
//   TextField,
//   Typography,
//   Chip,
//   LinearProgress,
// } from "@mui/material";
// import { alpha, keyframes } from "@mui/material/styles";
// import axios from "axios";

// import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
// import Battery6BarRoundedIcon from "@mui/icons-material/Battery6BarRounded";
// import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
// import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
// import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
// import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
// import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
// import SouthRoundedIcon from "@mui/icons-material/SouthRounded";
// import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
// import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
// import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";

// import { Colors } from "../../utils/Colors";
// import HumanBodyImg from "../../assets/img/body.jpg";
// import FluidDistributionGraph from "./FluidDistributionGraph";

// const http = axios.create({
//   baseURL: "http://127.0.0.1:8000/api",
//   timeout: 15000,
// });

// const dripFall = keyframes`
//   0% { transform: translate(-50%, 0px) scale(0.75); opacity: 0.15; }
//   20% { opacity: 1; }
//   100% { transform: translate(-50%, 88px) scale(1.02); opacity: 0; }
// `;

// const urineWave = keyframes`
//   0% { transform: scaleY(0.35); opacity: 0.45; }
//   50% { transform: scaleY(1); opacity: 1; }
//   100% { transform: scaleY(0.35); opacity: 0.45; }
// `;

// const alarmFlash = keyframes`
//   0% { box-shadow: 0 0 0 ${alpha(Colors.status?.danger || "#d32f2f", 0)}; }
//   50% { box-shadow: 0 0 14px ${alpha(Colors.status?.danger || "#d32f2f", 0.35)}; }
//   100% { box-shadow: 0 0 0 ${alpha(Colors.status?.danger || "#d32f2f", 0)}; }
// `;

// const bodyPulse = keyframes`
//   0% { transform: scale(1); opacity: 0.95; }
//   50% { transform: scale(1.015); opacity: 1; }
//   100% { transform: scale(1); opacity: 0.95; }
// `;

// const flowDash = keyframes`
//   0% { background-position: 0 0; }
//   100% { background-position: 24px 0; }
// `;

// const bubbleRise = keyframes`
//   0% { transform: translateY(8px); opacity: 0.25; }
//   50% { opacity: 1; }
//   100% { transform: translateY(-10px); opacity: 0; }
// `;

// const glowPulse = keyframes`
//   0% { opacity: 0.5; transform: translateX(-50%) scale(0.96); }
//   50% { opacity: 1; transform: translateX(-50%) scale(1.04); }
//   100% { opacity: 0.5; transform: translateX(-50%) scale(0.96); }
// `;

// const glowPulseCenter = keyframes`
//   0% { opacity: 0.5; transform: scale(0.96); }
//   50% { opacity: 1; transform: scale(1.04); }
//   100% { opacity: 0.5; transform: scale(0.96); }
// `;

// function fmt(v) {
//   if (v === undefined || v === null || v === "") return "—";
//   return String(v);
// }

// function toArray(payload) {
//   if (Array.isArray(payload)) return payload;
//   if (Array.isArray(payload?.results)) return payload.results;
//   if (Array.isArray(payload?.data)) return payload.data;
//   return [];
// }

// function pick(obj, keys, fallback = "") {
//   if (!obj) return fallback;
//   for (const key of keys) {
//     const value = obj?.[key];
//     if (value !== undefined && value !== null && value !== "") return value;
//   }
//   return fallback;
// }

// function normalizeKey(v) {
//   return String(v ?? "").trim().toLowerCase().replace(/\s+/g, "");
// }

// function extractDigits(v) {
//   const m = String(v ?? "").match(/\d+/);
//   return m ? m[0] : "";
// }

// function getAssignmentId(row) {
//   return pick(row, ["device_assignment_id", "assignment_id", "id"]);
// }

// function getDeviceSerial(row) {
//   return pick(row, [
//     "device_serial_number",
//     "device_serial",
//     "serial_number",
//     "device_number",
//     "device_code",
//     "device_id",
//     "device",
//   ]);
// }

// function getAssignmentAdmissionNumber(row) {
//   return pick(row, [
//     "admission_number",
//     "admission_no",
//     "admission_id",
//     "admission",
//     "admission_ref",
//     "admission_code",
//   ]);
// }

// function getAdmissionNumber(row) {
//   return pick(row, [
//     "admission_number",
//     "admission_no",
//     "admission_id",
//     "admission",
//     "admission_ref",
//     "admission_code",
//   ]);
// }

// function getAdmissionId(row) {
//   return pick(row, ["id", "admission_uuid", "uuid", "admission_id"]);
// }

// function getPatientName(adm) {
//   return pick(adm, ["patient_name", "full_name", "name", "patient_full_name"]);
// }

// function getAdmissionUhid(adm) {
//   return pick(adm, [
//     "patient_uhid_out",
//     "uhid",
//     "uid",
//     "patient_uid",
//     "patient_uhid",
//     "uhid_number",
//   ]);
// }

// function getPatientPossibleUhids(patient) {
//   return [
//     patient?.uhid,
//     patient?.patient_uhid_out,
//     patient?.patient_uhid,
//     patient?.uhid_number,
//     patient?.uid,
//   ]
//     .filter((x) => x !== undefined && x !== null && x !== "")
//     .map((x) => String(x).trim());
// }

// function getAge(patient, admission) {
//   return pick(
//     patient,
//     ["age", "patient_age", "current_age", "age_years"],
//     pick(admission, ["age", "patient_age"])
//   );
// }

// function getSex(patient, admission) {
//   return pick(
//     patient,
//     ["sex", "gender", "patient_gender"],
//     pick(admission, ["sex", "gender", "patient_gender"])
//   );
// }

// function getDoctor(adm) {
//   return pick(adm, [
//     "treating_doctor",
//     "consultant",
//     "doctor_name",
//     "consultant_name",
//     "doctor",
//     "doctor_assigned",
//     "attending_doctor",
//   ]);
// }

// function getWard(assignment, admission) {
//   return (
//     pick(assignment, ["ward_name", "ward", "ward_label", "ward_unit"]) ||
//     pick(admission, ["ward_unit", "ward_name", "ward"])
//   );
// }

// function getBed(assignment, admission) {
//   return (
//     pick(assignment, ["bed_number", "bed_no", "bed", "bed_label", "room_label"]) ||
//     pick(admission, ["bed_number", "bed_no", "bed", "room_label"])
//   );
// }

// function getHospitalName(admission, hospitalMap) {
//   const hospitalId = pick(admission, [
//     "hospital_id",
//     "hospital",
//     "organization",
//     "organization_id",
//   ]);
//   if (!hospitalId) return "";
//   return hospitalMap.get(String(hospitalId)) || String(hospitalId);
// }

// function normalizeFluidTherapy(row) {
//   if (!row) return null;

//   return {
//     id: row.id,
//     admission: pick(row, ["admission", "admission_id"]),
//     admission_number: pick(row, ["admission_number", "admission_no"]),
//     patient_uhid: pick(row, ["patient_uhid", "patient_uhid_out", "uhid"]),
//     patient_name: pick(row, ["patient_name"]),
//     doctor_name: pick(row, ["doctor_name"]),
//     fluid: pick(row, ["fluid"]),
//     fluid_label: pick(row, ["fluid_label"]),
//     qty_ml: Number(pick(row, ["qty_ml"], 0)) || 0,
//     qty_label: pick(row, ["qty_label"]),
//     speed_ml_hr: Number(pick(row, ["speed_ml_hr"], 0)) || 0,
//     frequency: pick(row, ["frequency"]),
//     duration: pick(row, ["duration"]),
//     created_at: pick(row, ["created_at"]),
//   };
// }

// function normalizeTelemetry(row) {
//   if (!row) return null;

//   return {
//     id: pick(row, ["id"]),
//     device_id: String(pick(row, ["device_id", "id"], "")).trim(),
//     fluid: pick(row, ["fluid"]),
//     bag_size: Number(pick(row, ["bag_size"], 0)) || 0,
//     drops: Number(pick(row, ["drops"], 0)) || 0,
//     dpm: Number(pick(row, ["dpm"], 0)) || 0,
//     target_dpm: Number(pick(row, ["target_dpm"], 0)) || 0,
//     flow_in: Number(pick(row, ["flow_in"], 0)) || 0,
//     remaining: Number(pick(row, ["remaining"], 0)) || 0,
//     alarm: pick(row, ["alarm"], "NORMAL"),
//     status: pick(row, ["status"], "UNKNOWN"),
//     run: Number(pick(row, ["run"], 0)) || 0,
//     session: Number(pick(row, ["session"], 0)) || 0,
//     rssi: Number(pick(row, ["rssi"], 0)) || 0,
//     created_at: pick(row, ["created_at"]),
//     payload: pick(row, ["payload"], null),
//   };
// }

// function estimateBatteryFromRssi(rssi) {
//   const n = Number(rssi);
//   if (Number.isNaN(n)) return 0;
//   if (n >= -55) return 100;
//   if (n >= -60) return 90;
//   if (n >= -65) return 80;
//   if (n >= -70) return 70;
//   if (n >= -75) return 60;
//   if (n >= -80) return 45;
//   if (n >= -85) return 30;
//   return 20;
// }

// function computeEtaFromTelemetry(remaining, dpm) {
//   const rem = Number(remaining) || 0;
//   const rate = Number(dpm) || 0;

//   if (rem <= 0 || rate <= 0) return "—";

//   const hours = rem / rate;
//   const wholeHours = Math.floor(hours);
//   const minutes = Math.round((hours - wholeHours) * 60);

//   const hh = String(wholeHours).padStart(2, "0");
//   const mm = String(minutes).padStart(2, "0");
//   return `${hh}:${mm} hr`;
// }

// function normalizeLiveStatus(status, dpm, alarm) {
//   const s = String(status || "").trim().toUpperCase();
//   const a = String(alarm || "").trim().toUpperCase();
//   const rate = Number(dpm) || 0;

//   if (s === "RUNNING") return "RUNNING";
//   if (s === "NO FLOW" || s === "FLOW STOP" || a === "NO FLOW" || a === "FLOW STOP") {
//     return "ON_HOLD";
//   }
//   if (s === "COMPLETED") return "COMPLETED";
//   if (rate > 0) return "RUNNING";
//   return "ON_HOLD";
// }

// function findTelemetryForDevice(telemetryMap, assignment) {
//   const serial = getDeviceSerial(assignment);
//   const fullKey = normalizeKey(serial);
//   const digitKey = normalizeKey(extractDigits(serial));

//   return telemetryMap.get(fullKey) || telemetryMap.get(digitKey) || null;
// }

// function buildMergedRow(
//   assignment,
//   admission,
//   patient,
//   hospitalMap,
//   index,
//   fluidTherapy,
//   telemetry
// ) {
//   const safeAdmission = admission || {};
//   const safePatient = patient || {};

//   const liveFlowRate = Number(telemetry?.dpm || 0);
//   const fluidIn = Number(telemetry?.flow_in || 0);
//   const fluidOut = 0;
//   const battery = estimateBatteryFromRssi(telemetry?.rssi);
//   const alarm = telemetry?.alarm || "NORMAL";

//   const prescribedSpeed = Number(
//     telemetry?.target_dpm || fluidTherapy?.speed_ml_hr || 0
//   );
//   const bottleMl = Number(telemetry?.bag_size || fluidTherapy?.qty_ml || 0);

//   const status = normalizeLiveStatus(telemetry?.status, telemetry?.dpm, telemetry?.alarm);

//   return {
//     id: getAssignmentId(assignment) || `${getDeviceSerial(assignment)}-${index}`,
//     deviceSerial: getDeviceSerial(assignment),
//     admissionId: getAdmissionId(safeAdmission),
//     admissionNumber:
//       getAssignmentAdmissionNumber(assignment) || getAdmissionNumber(safeAdmission),

//     patientName: getPatientName(safeAdmission),
//     uhid: getAdmissionUhid(safeAdmission),
//     age: getAge(safePatient, safeAdmission),
//     sex: getSex(safePatient, safeAdmission),
//     doctorName: getDoctor(safeAdmission),
//     hospitalName: getHospitalName(safeAdmission, hospitalMap),
//     wardName: getWard(assignment, safeAdmission),
//     bedNumber: getBed(assignment, safeAdmission),

//     assignedAt: pick(assignment, ["assigned_at", "created_at", "updated_at"]),
//     status,
//     battery,
//     network: telemetry ? "ONLINE" : "OFFLINE",

//     flowRate: liveFlowRate,
//     prescribedSpeed,
//     bottleMl,
//     bottleLabel: fluidTherapy?.qty_label || (bottleMl ? `${bottleMl} ml` : "—"),

//     totalVolume: bottleMl || 1000,
//     fluidIn,
//     fluidOut,
//     netBalance: (Number(fluidIn) || 0) - (Number(fluidOut) || 0),
//     eta: computeEtaFromTelemetry(telemetry?.remaining, telemetry?.dpm),
//     alarm,
//     lastUpdate: telemetry?.created_at
//       ? new Date(telemetry.created_at).toLocaleTimeString([], {
//           hour: "2-digit",
//           minute: "2-digit",
//         })
//       : "—",

//     fluidName: telemetry?.fluid || fluidTherapy?.fluid_label || fluidTherapy?.fluid || "—",
//     telemetry: telemetry || null,
//     fluidTherapy: fluidTherapy || null,
//   };
// }

// async function getMappedRows() {
//   const [
//     assignmentRes,
//     admissionRes,
//     patientRes,
//     hospitalRes,
//     therapyRes,
//     telemetryRes,
//   ] = await Promise.all([
//     http.get("/devices/device-assignments/?format=json"),
//     http.get("/patients/admissions/?format=json"),
//     http.get("/patients/patients/?format=json"),
//     http.get("/hospitals/?format=json"),
//     http.get("/medcharts/fluid-therapy/?format=json"),
//     http.get("/telemetry/latest/"),
//   ]);

//   const assignments = toArray(assignmentRes.data);
//   const admissions = toArray(admissionRes.data);
//   const patients = toArray(patientRes.data);
//   const hospitals = toArray(hospitalRes.data);
//   const fluidTherapies = toArray(therapyRes.data).map(normalizeFluidTherapy);
//   const telemetryRows = toArray(telemetryRes.data).map(normalizeTelemetry);

//   const admissionMap = new Map();
//   admissions.forEach((adm) => {
//     const byNumber = normalizeKey(getAdmissionNumber(adm));
//     if (byNumber) admissionMap.set(byNumber, adm);
//   });

//   const patientMap = new Map();
//   patients.forEach((patient) => {
//     getPatientPossibleUhids(patient).forEach((uhid) => {
//       patientMap.set(normalizeKey(uhid), patient);
//     });
//   });

//   const hospitalMap = new Map();
//   hospitals.forEach((hospital) => {
//     const id = pick(hospital, ["hospital_id", "id"]);
//     const name = pick(hospital, ["name", "hospital_name"]);
//     if (id && name) hospitalMap.set(String(id), name);
//   });

//   const telemetryMap = new Map();
//   telemetryRows.forEach((row) => {
//     const key1 = normalizeKey(row?.device_id);
//     const key2 = normalizeKey(extractDigits(row?.device_id));
//     if (key1) telemetryMap.set(key1, row);
//     if (key2) telemetryMap.set(key2, row);
//   });

//   return assignments.map((assignment, index) => {
//     const assignmentAdmissionNumber = normalizeKey(getAssignmentAdmissionNumber(assignment));
//     const admission = admissionMap.get(assignmentAdmissionNumber) || null;

//     const patientUhid = normalizeKey(getAdmissionUhid(admission || {}));
//     const admissionNumber = normalizeKey(getAdmissionNumber(admission || {}));
//     const patient = patientMap.get(patientUhid) || null;

//     const matchedTherapy =
//       fluidTherapies.find(
//         (therapy) =>
//           normalizeKey(therapy.patient_uhid) === patientUhid && patientUhid
//       ) ||
//       fluidTherapies.find(
//         (therapy) =>
//           normalizeKey(therapy.admission_number) === admissionNumber && admissionNumber
//       ) ||
//       null;

//     const matchedTelemetry = findTelemetryForDevice(telemetryMap, assignment);

//     return buildMergedRow(
//       assignment,
//       admission,
//       patient,
//       hospitalMap,
//       index,
//       matchedTherapy,
//       matchedTelemetry
//     );
//   });
// }

// function statusBg(status) {
//   if (status === "RUNNING") return Colors.status.success;
//   if (status === "ON_HOLD") return Colors.status.warning;
//   if (status === "COMPLETED") return Colors.status.info;
//   return Colors.text.sub;
// }

// function Panel({ title, children, right, sx = {} }) {
//   return (
//     <Card
//       sx={{
//         borderRadius: 3,
//         color: Colors.text.body,
//         background: Colors.bg.paper,
//         border: `1px solid ${Colors.border.light}`,
//         boxShadow: Colors.shadow.brand,
//         height: "100%",
//         ...sx,
//       }}
//     >
//       <CardContent sx={{ p: { xs: 1.3, md: 1.6 } }}>
//         <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
//           <Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 900, color: Colors.text.title }}>
//             {title}
//           </Typography>
//           {right}
//         </Stack>
//         <Divider sx={{ borderColor: Colors.border.light, mb: 1.1 }} />
//         {children}
//       </CardContent>
//     </Card>
//   );
// }

// function DetailLine({ label, value }) {
//   return (
//     <Stack
//       direction="row"
//       spacing={1}
//       alignItems="flex-start"
//       sx={{ py: 0.42, borderBottom: `1px solid ${Colors.border.topLine}` }}
//     >
//       <Typography sx={{ width: 82, minWidth: 82, fontSize: 11, fontWeight: 800, color: Colors.text.muted }}>
//         {label}
//       </Typography>
//       <Typography
//         sx={{
//           fontSize: 13,
//           fontWeight: 800,
//           color: Colors.text.body,
//           wordBreak: "break-word",
//           lineHeight: 1.25,
//         }}
//       >
//         {fmt(value)}
//       </Typography>
//     </Stack>
//   );
// }

// function InfoLabel({ label, value, big = false }) {
//   return (
//     <Box>
//       <Typography sx={{ fontSize: 11, color: Colors.text.muted, fontWeight: 700 }}>
//         {label}
//       </Typography>
//       <Typography
//         sx={{
//           mt: 0.2,
//           fontSize: big ? { xs: 18, md: 22 } : { xs: 14, md: 16 },
//           fontWeight: 900,
//           color: Colors.text.body,
//           lineHeight: 1.1,
//           wordBreak: "break-word",
//         }}
//       >
//         {fmt(value)}
//       </Typography>
//     </Box>
//   );
// }

// function AnimatedDripBottle({ speed = 75, active = false }) {
//   const duration = Math.max(0.6, 2.6 - speed / 65);

//   return (
//     <Box sx={{ position: "relative", width: 96, height: 138, mx: "auto", opacity: active ? 1 : 0.55 }}>
//       <Box
//         sx={{
//           position: "absolute",
//           top: 0,
//           left: "50%",
//           transform: "translateX(-50%)",
//           width: 16,
//           height: 16,
//           borderRadius: "6px 6px 0 0",
//           border: `2px solid ${Colors.brand.primary}`,
//           borderBottom: "none",
//           bgcolor: Colors.bg.paper,
//         }}
//       />
//       <Box
//         sx={{
//           position: "absolute",
//           top: 12,
//           left: "50%",
//           transform: "translateX(-50%)",
//           width: 62,
//           height: 68,
//           borderRadius: "14px 14px 16px 16px",
//           border: `2px solid ${Colors.brand.primary}`,
//           bgcolor: alpha(Colors.brand.primary, 0.05),
//           overflow: "hidden",
//         }}
//       >
//         <Box
//           sx={{
//             position: "absolute",
//             left: 0,
//             right: 0,
//             bottom: 0,
//             height: "58%",
//             bgcolor: alpha(Colors.status.success, 0.22),
//             borderTop: `2px solid ${Colors.status.success}`,
//           }}
//         />
//       </Box>
//       <Box
//         sx={{
//           position: "absolute",
//           top: 82,
//           left: "50%",
//           transform: "translateX(-50%)",
//           width: 20,
//           height: 24,
//           borderRadius: "9px 9px 11px 11px",
//           border: `2px solid ${Colors.brand.primary}`,
//           bgcolor: alpha(Colors.brand.primary, 0.04),
//         }}
//       />
//       <Box
//         sx={{
//           position: "absolute",
//           top: 104,
//           left: "50%",
//           transform: "translateX(-50%)",
//           width: 3,
//           height: 26,
//           bgcolor: alpha(Colors.brand.primary, active ? 0.75 : 0.25),
//           borderRadius: 999,
//         }}
//       />
//       {[0, 0.5, 1].map((delay, i) => (
//         <Box
//           key={i}
//           sx={{
//             position: "absolute",
//             top: 95,
//             left: "50%",
//             width: 8,
//             height: 12,
//             borderRadius: "50% 50% 55% 55%",
//             bgcolor: Colors.brand.primary,
//             animation: active ? `${dripFall} ${duration}s linear ${delay}s infinite` : "none",
//             opacity: active ? 1 : 0,
//           }}
//         />
//       ))}
//       <Typography
//         sx={{
//           position: "absolute",
//           bottom: -4,
//           left: "50%",
//           transform: "translateX(-50%)",
//           fontSize: 11,
//           fontWeight: 800,
//           color: active ? Colors.status.success : Colors.text.muted,
//         }}
//       >
//         {active ? "Drip Running" : "Drip Stopped"}
//       </Typography>
//     </Box>
//   );
// }

// function AnimatedUrineBag({ value = 0, active = false }) {
//   const fillPercent = Math.max(12, Math.min(85, Math.round((Number(value) || 0) / 6)));
//   const urineYellow = "#D9B63D";

//   return (
//     <Box sx={{ position: "relative", width: 110, height: 150, mx: "auto", opacity: active ? 1 : 0.7 }}>
//       <Box
//         sx={{
//           position: "absolute",
//           top: 0,
//           left: "50%",
//           transform: "translateX(-50%)",
//           width: 22,
//           height: 18,
//           border: `2px solid ${Colors.brand.primary}`,
//           borderBottom: "none",
//           borderRadius: "10px 10px 0 0",
//           bgcolor: Colors.bg.paper,
//         }}
//       />
//       <Box
//         sx={{
//           position: "absolute",
//           top: 16,
//           left: "50%",
//           transform: "translateX(-50%)",
//           width: 78,
//           height: 110,
//           border: `2px solid ${Colors.brand.primary}`,
//           borderRadius: "16px 16px 18px 18px",
//           overflow: "hidden",
//           bgcolor: alpha(Colors.brand.primary, 0.04),
//         }}
//       >
//         <Box
//           sx={{
//             position: "absolute",
//             left: 0,
//             right: 0,
//             bottom: 0,
//             height: `${fillPercent}%`,
//             bgcolor: alpha(urineYellow, 0.46),
//             borderTop: `2px solid ${urineYellow}`,
//             animation: active ? `${urineWave} 1.6s ease-in-out infinite` : "none",
//             transformOrigin: "bottom",
//           }}
//         />
//         <Stack
//           justifyContent="center"
//           alignItems="center"
//           sx={{ position: "absolute", inset: 0, textAlign: "center", px: 1 }}
//         >
//           <Typography sx={{ fontSize: 22, fontWeight: 900, color: Colors.text.title }}>
//             {fmt(value)}
//           </Typography>
//           <Typography sx={{ fontSize: 14, fontWeight: 800, color: Colors.text.body }}>
//             ml
//           </Typography>
//         </Stack>
//       </Box>
//       <Typography
//         sx={{
//           position: "absolute",
//           bottom: -4,
//           left: "50%",
//           transform: "translateX(-50%)",
//           fontSize: 11,
//           fontWeight: 800,
//           color: active ? urineYellow : Colors.text.muted,
//         }}
//       >
//         {active ? "Output Updating" : "Output Stable"}
//       </Typography>
//     </Box>
//   );
// }

// function AlarmPanel({ alarm }) {
//   const danger = alarm && alarm !== "NORMAL";

//   return (
//     <Card
//       sx={{
//         borderRadius: 3,
//         border: `1px solid ${Colors.border.light}`,
//         background: danger ? alpha(Colors.status.danger, 0.08) : Colors.bg.paper,
//         boxShadow: Colors.shadow.brand,
//         height: "100%",
//         animation: danger ? `${alarmFlash} 1.3s infinite` : "none",
//       }}
//     >
//       <CardContent sx={{ p: { xs: 1.3, md: 1.6 } }}>
//         <Stack spacing={1}>
//           <Typography sx={{ fontSize: 15, fontWeight: 900, color: Colors.text.title }}>
//             Alarm Panel
//           </Typography>
//           <Divider sx={{ borderColor: Colors.border.light }} />
//           <Chip
//             icon={danger ? <WarningAmberRoundedIcon sx={{ color: `${Colors.bg.paper} !important` }} /> : undefined}
//             label={alarm || "NORMAL"}
//             sx={{
//               bgcolor: danger ? Colors.status.danger : Colors.status.success,
//               color: Colors.bg.paper,
//               fontWeight: 900,
//               width: "fit-content",
//             }}
//           />
//           <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
//             {danger ? "Attention required. Check IV line or flow." : "No active alarm."}
//           </Typography>
//         </Stack>
//       </CardContent>
//     </Card>
//   );
// }

// function TherapyValueCard({ label, value }) {
//   return (
//     <Box
//       sx={{
//         p: 1.15,
//         borderRadius: 2,
//         border: `1px solid ${Colors.border.light}`,
//         bgcolor: alpha(Colors.brand.primary, 0.03),
//         minHeight: 74,
//       }}
//     >
//       <Typography sx={{ fontSize: 11, color: Colors.text.muted, fontWeight: 700 }}>
//         {label}
//       </Typography>
//       <Typography
//         sx={{
//           mt: 0.55,
//           fontSize: 15,
//           fontWeight: 900,
//           color: Colors.text.title,
//           lineHeight: 1.2,
//           wordBreak: "break-word",
//         }}
//       >
//         {fmt(value)}
//       </Typography>
//     </Box>
//   );
// }

// function FluidTherapyInfoCard({ row }) {
//   const therapy = row?.fluidTherapy;

//   return (
//     <Panel
//       title="Fluid Therapy Information"
//       right={<MedicationOutlinedIcon sx={{ color: Colors.text.icon }} />}
//       sx={{ background: alpha(Colors.brand.primary, 0.04) }}
//     >
//       {!therapy ? (
//         <Alert severity="info" sx={{ borderRadius: 2 }}>
//           No fluid therapy information found for this patient.
//         </Alert>
//       ) : (
//         <Grid container spacing={1.2}>
//           <Grid item xs={12} sm={6} md={3}>
//             <TherapyValueCard label="Patient Name" value={therapy.patient_name} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={3}>
//             <TherapyValueCard label="Doctor Name" value={therapy.doctor_name} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={3}>
//             <TherapyValueCard label="Fluid Code" value={therapy.fluid} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={3}>
//             <TherapyValueCard label="Fluid Name" value={therapy.fluid_label} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={3}>
//             <TherapyValueCard label="Bottle Size" value={therapy.qty_label || `${therapy.qty_ml} ml`} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={3}>
//             <TherapyValueCard label="Bottle ml" value={therapy.qty_ml} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={3}>
//             <TherapyValueCard label="Speed ml/hr" value={therapy.speed_ml_hr} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={3}>
//             <TherapyValueCard label="Frequency" value={therapy.frequency} />
//           </Grid>
//           <Grid item xs={12} sm={6} md={3}>
//             <TherapyValueCard label="Duration" value={therapy.duration} />
//           </Grid>
//         </Grid>
//       )}
//     </Panel>
//   );
// }

// function HumanFluidBalanceChart({ fluidIn = 0, fluidOut = 0, netBalance = 0 }) {
//   const safeIn = Number(fluidIn) || 0;
//   const safeOut = Number(fluidOut) || 0;
//   const safeNet = Number(netBalance) || 0;
//   const positive = safeNet >= 0;

//   const intakeWidth = Math.max(12, Math.min(100, Math.round(safeIn / 10)));
//   const outputWidth = Math.max(12, Math.min(100, Math.round(safeOut / 10)));

//   const urineYellow = "#D9B63D";
//   const urineYellowSoft = alpha(urineYellow, 0.16);

//   return (
//     <Box
//       sx={{
//         position: "relative",
//         width: "100%",
//         minHeight: { xs: 320, md: 340 },
//         borderRadius: 3,
//         border: `1px solid ${Colors.border.light}`,
//         bgcolor: "#F7F8F8",
//         overflow: "hidden",
//         px: { xs: 1, md: 1.4 },
//         py: { xs: 1.2, md: 1.5 },
//       }}
//     >
//       <Grid container spacing={1.2} alignItems="center">
//         <Grid item xs={12} md={3}>
//           <Stack spacing={1} alignItems={{ xs: "center", md: "flex-start" }}>
//             <Typography sx={{ fontSize: 13, fontWeight: 900, color: Colors.text.title }}>
//               Fluid In
//             </Typography>

//             <Box
//               sx={{
//                 width: "100%",
//                 maxWidth: 160,
//                 p: 1,
//                 borderRadius: 2,
//                 border: `1px solid ${alpha(Colors.status.success, 0.25)}`,
//                 bgcolor: alpha(Colors.status.success, 0.14),
//                 boxShadow: `0 8px 20px ${alpha(Colors.status.success, 0.08)}`,
//               }}
//             >
//               <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 700 }}>
//                 Input
//               </Typography>
//               <Typography sx={{ fontSize: 16, fontWeight: 800, color: Colors.text.body }}>
//                 {fmt(safeIn)} ml
//               </Typography>
//             </Box>

//             <Box
//               sx={{
//                 width: "100%",
//                 maxWidth: 160,
//                 height: 9,
//                 borderRadius: 999,
//                 bgcolor: alpha(Colors.status.success, 0.12),
//                 overflow: "hidden",
//               }}
//             >
//               <Box
//                 sx={{
//                   width: `${intakeWidth}%`,
//                   height: "100%",
//                   bgcolor: Colors.status.success,
//                   borderRadius: 999,
//                   transition: "width 0.5s ease",
//                 }}
//               />
//             </Box>
//           </Stack>
//         </Grid>

//         <Grid item xs={12} md={6}>
//           <Box
//             sx={{
//               position: "relative",
//               width: "100%",
//               height: { xs: 290, md: 310 },
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//             }}
//           >
//             <Box
//               sx={{
//                 position: "absolute",
//                 left: { xs: "4%", md: "2%" },
//                 top: { xs: "28%", md: "29%" },
//                 width: { xs: "28%", md: "30%" },
//                 height: 4,
//                 backgroundImage: `linear-gradient(90deg, ${Colors.status.success} 50%, transparent 50%)`,
//                 backgroundSize: "14px 4px",
//                 backgroundRepeat: "repeat-x",
//                 animation: `${flowDash} 1.1s linear infinite`,
//                 "&::after": {
//                   content: '""',
//                   position: "absolute",
//                   right: -2,
//                   top: -4,
//                   borderTop: "6px solid transparent",
//                   borderBottom: "6px solid transparent",
//                   borderLeft: `10px solid ${Colors.status.success}`,
//                 },
//               }}
//             />

//             <Box
//               sx={{
//                 position: "absolute",
//                 right: { xs: "4%", md: "2%" },
//                 top: { xs: "52%", md: "53%" },
//                 width: { xs: "28%", md: "30%" },
//                 height: 4,
//                 backgroundImage: `linear-gradient(90deg, ${urineYellow} 50%, transparent 50%)`,
//                 backgroundSize: "14px 4px",
//                 backgroundRepeat: "repeat-x",
//                 animation: `${flowDash} 1.1s linear infinite`,
//                 "&::before": {
//                   content: '""',
//                   position: "absolute",
//                   left: -2,
//                   top: -4,
//                   borderTop: "6px solid transparent",
//                   borderBottom: "6px solid transparent",
//                   borderRight: `10px solid ${urineYellow}`,
//                 },
//               }}
//             />

//             <Box
//               sx={{
//                 position: "relative",
//                 width: { xs: 150, sm: 170, md: 185 },
//                 height: { xs: 240, sm: 265, md: 285 },
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 animation: `${bodyPulse} 2.6s ease-in-out infinite`,
//               }}
//             >
//               <Box
//                 component="img"
//                 src={HumanBodyImg}
//                 alt="Human Body"
//                 sx={{
//                   width: "100%",
//                   height: "100%",
//                   objectFit: "contain",
//                   display: "block",
//                   userSelect: "none",
//                   pointerEvents: "none",
//                   filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.08))",
//                 }}
//               />

//               <Box
//                 sx={{
//                   position: "absolute",
//                   left: "50%",
//                   top: { xs: "24%", md: "23%" },
//                   transform: "translateX(-50%)",
//                   width: { xs: 34, md: 40 },
//                   height: { xs: 72, md: 82 },
//                   borderRadius: "50% 50% 45% 45%",
//                   bgcolor: alpha(Colors.status.success, 0.25),
//                   border: `2px solid ${Colors.status.success}`,
//                   boxShadow: `0 0 18px ${alpha(Colors.status.success, 0.3)}`,
//                   animation: `${glowPulse} 2s ease-in-out infinite`,
//                   overflow: "hidden",
//                 }}
//               >
//                 <Box
//                   sx={{
//                     position: "absolute",
//                     left: "50%",
//                     top: "50%",
//                     transform: "translate(-50%, -50%)",
//                     width: "58%",
//                     height: "58%",
//                     borderRadius: "50% 50% 55% 55%",
//                     border: `1.5px solid ${alpha(Colors.status.success, 0.95)}`,
//                     bgcolor: alpha(Colors.status.success, 0.1),
//                   }}
//                 />
//                 <Box
//                   sx={{
//                     position: "absolute",
//                     left: "50%",
//                     top: "20%",
//                     bottom: "18%",
//                     width: 1.5,
//                     transform: "translateX(-50%)",
//                     bgcolor: alpha(Colors.status.success, 0.45),
//                     borderRadius: 999,
//                     borderLeft: `1px dashed ${alpha(Colors.status.success, 0.55)}`,
//                   }}
//                 />
//               </Box>

//               <Box
//                 sx={{
//                   position: "absolute",
//                   left: "50%",
//                   top: { xs: "55%", md: "56%" },
//                   transform: "translateX(-50%)",
//                   width: { xs: 24, md: 28 },
//                   height: { xs: 24, md: 28 },
//                   borderRadius: "50%",
//                   bgcolor: alpha(urineYellow, 0.3),
//                   border: `2px solid ${urineYellow}`,
//                   boxShadow: `0 0 14px ${alpha(urineYellow, 0.26)}`,
//                   animation: `${glowPulseCenter} 1.8s ease-in-out infinite`,
//                 }}
//               >
//                 <Box
//                   sx={{
//                     position: "absolute",
//                     left: "50%",
//                     top: "50%",
//                     width: "42%",
//                     height: "52%",
//                     transform: "translate(-50%, -50%)",
//                     borderRadius: "50% 50% 60% 60%",
//                     border: `1.2px solid ${alpha(urineYellow, 0.95)}`,
//                     bgcolor: alpha(urineYellow, 0.1),
//                   }}
//                 />
//               </Box>

//               {[0, 1, 2, 3].map((i) => (
//                 <Box
//                   key={`green-dot-${i}`}
//                   sx={{
//                     position: "absolute",
//                     left: `calc(50% - ${28 - i * 7}px)`,
//                     top: { xs: `${29 - i * 1.4}%`, md: `${28 - i * 1.4}%` },
//                     width: 6,
//                     height: 6,
//                     borderRadius: "50%",
//                     bgcolor: Colors.status.success,
//                     boxShadow: `0 0 8px ${alpha(Colors.status.success, 0.4)}`,
//                     animation: `${bubbleRise} ${1.2 + i * 0.18}s ease-in-out infinite`,
//                   }}
//                 />
//               ))}

//               {[0, 1, 2, 3].map((i) => (
//                 <Box
//                   key={`yellow-dot-${i}`}
//                   sx={{
//                     position: "absolute",
//                     left: `calc(50% + ${18 + i * 7}px)`,
//                     top: { xs: `${57 - i * 1.3}%`, md: `${57 - i * 1.3}%` },
//                     width: 5.5,
//                     height: 5.5,
//                     borderRadius: "50%",
//                     bgcolor: urineYellow,
//                     boxShadow: `0 0 8px ${alpha(urineYellow, 0.35)}`,
//                     animation: `${bubbleRise} ${1.2 + i * 0.18}s ease-in-out infinite`,
//                   }}
//                 />
//               ))}
//             </Box>

//             <Box
//               sx={{
//                 position: "absolute",
//                 bottom: 0,
//                 left: "50%",
//                 transform: "translateX(-50%)",
//                 px: 1.6,
//                 py: 0.55,
//                 borderRadius: 999,
//                 bgcolor: positive
//                   ? alpha(Colors.status.success, 0.14)
//                   : alpha(Colors.status.danger, 0.14),
//                 border: `1px solid ${
//                   positive
//                     ? alpha(Colors.status.success, 0.28)
//                     : alpha(Colors.status.danger, 0.28)
//                 }`,
//                 boxShadow: positive
//                   ? `0 8px 18px ${alpha(Colors.status.success, 0.12)}`
//                   : `0 8px 18px ${alpha(Colors.status.danger, 0.12)}`,
//               }}
//             >
//               <Typography
//                 sx={{
//                   fontSize: 12,
//                   fontWeight: 900,
//                   color: positive ? Colors.status.success : Colors.status.danger,
//                 }}
//               >
//                 Net {positive ? "+" : ""}
//                 {fmt(safeNet)} ml
//               </Typography>
//             </Box>
//           </Box>
//         </Grid>

//         <Grid item xs={12} md={3}>
//           <Stack spacing={1} alignItems={{ xs: "center", md: "flex-end" }}>
//             <Typography sx={{ fontSize: 13, fontWeight: 900, color: Colors.text.title }}>
//               Fluid Out
//             </Typography>

//             <Box
//               sx={{
//                 width: "100%",
//                 maxWidth: 160,
//                 p: 1,
//                 borderRadius: 2,
//                 border: `1px solid ${alpha(urineYellow, 0.3)}`,
//                 bgcolor: urineYellowSoft,
//                 boxShadow: `0 8px 20px ${alpha(urineYellow, 0.08)}`,
//               }}
//             >
//               <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 700 }}>
//                 Output
//               </Typography>
//               <Typography sx={{ fontSize: 16, fontWeight: 800, color: Colors.text.body }}>
//                 {fmt(safeOut)} ml
//               </Typography>
//             </Box>

//             <Box
//               sx={{
//                 width: "100%",
//                 maxWidth: 160,
//                 height: 9,
//                 borderRadius: 999,
//                 bgcolor: alpha(urineYellow, 0.16),
//                 overflow: "hidden",
//               }}
//             >
//               <Box
//                 sx={{
//                   width: `${outputWidth}%`,
//                   height: "100%",
//                   bgcolor: urineYellow,
//                   borderRadius: 999,
//                   transition: "width 0.5s ease",
//                 }}
//               />
//             </Box>
//           </Stack>
//         </Grid>
//       </Grid>
//     </Box>
//   );
// }

// export default function FluidMonitoringPage() {
//   const [rows, setRows] = React.useState([]);
//   const [selectedId, setSelectedId] = React.useState("");
//   const [loading, setLoading] = React.useState(true);
//   const [error, setError] = React.useState("");
//   const [animationState, setAnimationState] = React.useState({});
//   const prevRowsRef = React.useRef({});

//   const loadData = React.useCallback(async () => {
//     try {
//       setError("");
//       const data = await getMappedRows();
//       setRows(data);

//       const nextAnimationState = {};
//       const nextPrevRows = { ...prevRowsRef.current };

//       data.forEach((row) => {
//         const oldRow = prevRowsRef.current[row.id];

//         const fluidOutChanged =
//           oldRow && Number(row.fluidOut || 0) > Number(oldRow.fluidOut || 0);

//         const dripRunning =
//           row.status === "RUNNING" &&
//           Number(row.flowRate || 0) > 0 &&
//           row.alarm !== "FLOW STOP" &&
//           row.alarm !== "NO FLOW";

//         const urineRunning = Boolean(fluidOutChanged);

//         nextAnimationState[row.id] = {
//           dripRunning,
//           urineRunning,
//         };

//         nextPrevRows[row.id] = row;
//       });

//       prevRowsRef.current = nextPrevRows;
//       setAnimationState(nextAnimationState);

//       setSelectedId((prevSelectedId) => {
//         if (!prevSelectedId) return "";
//         const exists = data.some((r) => String(r.id) === String(prevSelectedId));
//         return exists ? prevSelectedId : "";
//       });

//       setLoading(false);
//     } catch (err) {
//       setError(
//         err?.response?.data?.detail ||
//           err?.response?.data?.message ||
//           err?.message ||
//           "Failed to load fluid monitoring page"
//       );
//       setLoading(false);
//     }
//   }, []);

//   React.useEffect(() => {
//     loadData();
//   }, [loadData]);

//   React.useEffect(() => {
//     const timer = setInterval(() => {
//       loadData();
//     }, 5000);
//     return () => clearInterval(timer);
//   }, [loadData]);

//   const selectedRow =
//     rows.find((r) => String(r.id) === String(selectedId)) || null;

//   const selectedAnimation =
//     (selectedRow && animationState[selectedRow.id]) || {
//       dripRunning: false,
//       urineRunning: false,
//     };

//   const selectedOption =
//     rows.find((row) => String(row.id) === String(selectedId)) || null;

//   return (
//     <Box sx={{ minHeight: "100vh", p: { xs: 1, md: 1.5 }, background: Colors.bg.app }}>
//       <Stack spacing={1.2}>
//         <Card
//           sx={{
//             borderRadius: 2,
//             p: 0.2,
//             color: Colors.text.body,
//             background: Colors.bg.paper,
//             border: `1px solid ${Colors.border.light}`,
//             boxShadow: Colors.shadow.brand,
//           }}
//         >
//           <CardContent sx={{ p: { xs: 1.2, md: 1.5 } }}>
//             <Stack
//               direction={{ xs: "column", lg: "row" }}
//               justifyContent="space-between"
//               alignItems={{ xs: "flex-start", lg: "center" }}
//               spacing={1.5}
//             >
//               <Box>
//                 <Typography sx={{ fontSize: { xs: 20, md: 26 }, fontWeight: 900, color: Colors.text.title }}>
//                   FLUID MONITORING
//                 </Typography>
//                 <Typography sx={{ fontSize: 13, color: Colors.text.muted, mt: 0.4 }}>
//                   IV flow, urine out and fluid balance monitoring
//                 </Typography>
//               </Box>

//               <Stack
//                 direction={{ xs: "column", sm: "row" }}
//                 spacing={1.2}
//                 alignItems={{ xs: "stretch", sm: "center" }}
//                 sx={{ width: { xs: "100%", lg: "auto" } }}
//               >
//                 <Autocomplete
//                   fullWidth
//                   options={rows}
//                   value={selectedOption}
//                   onChange={(_, newValue) => {
//                     setSelectedId(newValue ? String(newValue.id) : "");
//                   }}
//                   isOptionEqualToValue={(option, value) =>
//                     String(option?.id) === String(value?.id)
//                   }
//                   getOptionLabel={(option) => {
//                     if (!option) return "";
//                     const patient = option.patientName || "No Patient";
//                     const serial = option.deviceSerial || "No Device";
//                     const uhid = option.uhid || "No UHID";
//                     return `${patient} | ${serial} | ${uhid}`;
//                   }}
//                   filterOptions={(options, state) => {
//                     const search = state.inputValue.trim().toLowerCase();
//                     if (!search) return options;

//                     return options.filter((option) => {
//                       const patient = String(option.patientName || "").toLowerCase();
//                       const serial = String(option.deviceSerial || "").toLowerCase();
//                       const uhid = String(option.uhid || "").toLowerCase();
//                       const bed = String(option.bedNumber || "").toLowerCase();
//                       const doctor = String(option.doctorName || "").toLowerCase();
//                       const admission = String(option.admissionNumber || "").toLowerCase();

//                       return (
//                         patient.includes(search) ||
//                         serial.includes(search) ||
//                         uhid.includes(search) ||
//                         bed.includes(search) ||
//                         doctor.includes(search) ||
//                         admission.includes(search)
//                       );
//                     });
//                   }}
//                   noOptionsText="No patient / device found"
//                   sx={{
//                     minWidth: { xs: "100%", sm: 340 },
//                     "& .MuiOutlinedInput-root": {
//                       color: Colors.text.body,
//                       bgcolor: Colors.bg.paper,
//                       borderRadius: 2,
//                     },
//                     "& .MuiSvgIcon-root": { color: Colors.text.icon },
//                   }}
//                   renderInput={(params) => (
//                     <TextField
//                       {...params}
//                       size="small"
//                       label="Select Patient / Device"
//                       placeholder="Search patient name / UHID / device serial"
//                     />
//                   )}
//                   renderOption={(props, option) => (
//                     <Box component="li" {...props} key={option.id}>
//                       <Stack spacing={0.2}>
//                         <Typography
//                           sx={{
//                             fontSize: 14,
//                             fontWeight: 800,
//                             color: Colors.text.title,
//                           }}
//                         >
//                           {option.patientName || "No Patient"}
//                         </Typography>
//                         <Typography sx={{ fontSize: 12, color: Colors.text.body }}>
//                           Device: {option.deviceSerial || "—"} | UHID: {option.uhid || "—"}
//                         </Typography>
//                         <Typography sx={{ fontSize: 11, color: Colors.text.muted }}>
//                           Bed: {option.bedNumber || "—"} | Doctor: {option.doctorName || "—"}
//                         </Typography>
//                       </Stack>
//                     </Box>
//                   )}
//                 />

//                 {selectedRow && (
//                   <Chip
//                     label={selectedRow.status}
//                     sx={{
//                       bgcolor: statusBg(selectedRow.status),
//                       color: Colors.bg.paper,
//                       fontWeight: 900,
//                     }}
//                   />
//                 )}
//               </Stack>
//             </Stack>
//           </CardContent>
//         </Card>

//         {loading ? (
//           <Card
//             sx={{
//               borderRadius: 2,
//               background: Colors.bg.paper,
//               border: `1px solid ${Colors.border.light}`,
//               boxShadow: Colors.shadow.brand,
//             }}
//           >
//             <CardContent sx={{ py: 8, textAlign: "center" }}>
//               <CircularProgress sx={{ color: Colors.brand.primary }} />
//               <Typography sx={{ mt: 2, color: Colors.text.title, fontWeight: 800 }}>
//                 Loading fluid monitoring...
//               </Typography>
//             </CardContent>
//           </Card>
//         ) : error ? (
//           <Alert severity="error">{error}</Alert>
//         ) : !rows.length ? (
//           <Alert severity="info">No monitoring data found.</Alert>
//         ) : !selectedRow ? (
//           <Alert severity="info">Please select a patient / device from dropdown.</Alert>
//         ) : (
//           <>
//             <Card
//               sx={{
//                 borderRadius: 2,
//                 background: Colors.bg.paper,
//                 border: `1px solid ${Colors.border.light}`,
//                 boxShadow: Colors.shadow.brand,
//               }}
//             >
//               <CardContent sx={{ p: { xs: 1.2, md: 1.5 } }}>
//                 <Stack
//                   direction={{ xs: "column", md: "row" }}
//                   spacing={1}
//                   justifyContent="space-between"
//                   alignItems={{ xs: "flex-start", md: "center" }}
//                 >
//                   <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
//                     <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 900, color: Colors.text.title }}>
//                       Patient: {fmt(selectedRow.patientName)}
//                     </Typography>
//                     <Typography sx={{ color: Colors.text.muted, fontSize: 14 }}>
//                       | UHID: {fmt(selectedRow.uhid)} | Bed: {fmt(selectedRow.bedNumber)} | Age: {fmt(selectedRow.age)} / {fmt(selectedRow.sex)}
//                     </Typography>
//                   </Stack>

//                   <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
//                     <Stack direction="row" spacing={0.5} alignItems="center">
//                       <WifiRoundedIcon sx={{ fontSize: 16, color: Colors.status.success }} />
//                       <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
//                         {fmt(selectedRow.network)}
//                       </Typography>
//                     </Stack>
//                     <Stack direction="row" spacing={0.5} alignItems="center">
//                       <Battery6BarRoundedIcon sx={{ fontSize: 16, color: Colors.text.icon }} />
//                       <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
//                         Battery: {fmt(selectedRow.battery)}%
//                       </Typography>
//                     </Stack>
//                     <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
//                       Last Update: {fmt(selectedRow.lastUpdate)}
//                     </Typography>
//                     <NotificationsNoneRoundedIcon sx={{ fontSize: 18, color: Colors.text.icon }} />
//                   </Stack>
//                 </Stack>
//               </CardContent>
//             </Card>

//             <Grid container spacing={1.2}>
//               <Grid item xs={12} xl={3.2}>
//                 <Panel title="Patient Details" right={<PersonRoundedIcon sx={{ color: Colors.text.icon }} />}>
//                   <Stack spacing={0.2}>
//                     <DetailLine label="Name" value={selectedRow.patientName} />
//                     <DetailLine label="UHID" value={selectedRow.uhid} />
//                     <DetailLine label="Age" value={selectedRow.age} />
//                     <DetailLine label="Sex" value={selectedRow.sex} />
//                     <DetailLine label="Doctor" value={selectedRow.doctorName} />
//                     <DetailLine label="Ward" value={selectedRow.wardName} />
//                     <DetailLine label="Bed" value={selectedRow.bedNumber} />
//                     <DetailLine label="Hospital" value={selectedRow.hospitalName} />
//                     <DetailLine label="Admission" value={selectedRow.admissionNumber} />
//                   </Stack>
//                 </Panel>
//               </Grid>

//               <Grid item xs={12} md={6} xl={2.5}>
//                 <Panel
//                   title="Fluid In"
//                   right={<MemoryRoundedIcon sx={{ color: Colors.text.icon }} />}
//                   sx={{ background: alpha(Colors.status.success, 0.08) }}
//                 >
//                   <Stack spacing={1} alignItems="center" sx={{ textAlign: "center" }}>
//                     <AnimatedDripBottle
//                       speed={selectedRow.flowRate}
//                       active={selectedAnimation.dripRunning}
//                     />

//                     <Stack direction="row" spacing={1} alignItems="center">
//                       <WaterDropRoundedIcon sx={{ color: Colors.brand.primary }} />
//                       <Typography sx={{ fontSize: 15, fontWeight: 900, color: Colors.text.title }}>
//                         DRIP ON
//                       </Typography>
//                     </Stack>

//                     <Typography sx={{ fontSize: { xs: 34, md: 46 }, fontWeight: 900, lineHeight: 1, color: Colors.text.title }}>
//                       {fmt(selectedRow.flowRate)}
//                     </Typography>

//                     <Typography sx={{ fontSize: 15, fontWeight: 800, color: Colors.text.body }}>
//                       ml/hr
//                     </Typography>

//                     <Divider sx={{ width: "100%", borderColor: Colors.border.light }} />

//                     <Grid container spacing={1}>
//                       <Grid item xs={6}>
//                         <InfoLabel
//                           label="Speed"
//                           value={
//                             selectedRow.prescribedSpeed
//                               ? `${fmt(selectedRow.prescribedSpeed)} ml/hr`
//                               : "—"
//                           }
//                         />
//                       </Grid>
//                       <Grid item xs={6}>
//                         <InfoLabel
//                           label="Bottle"
//                           value={selectedRow.bottleLabel || "—"}
//                         />
//                       </Grid>
//                     </Grid>
//                   </Stack>
//                 </Panel>
//               </Grid>

//               <Grid item xs={12} xl={3.8}>
//                 <Panel
//                   title="Fluid Balance Chart"
//                   right={<LocalHospitalRoundedIcon sx={{ color: Colors.text.icon }} />}
//                 >
//                   <HumanFluidBalanceChart
//                     fluidIn={selectedRow.fluidIn}
//                     fluidOut={selectedRow.fluidOut}
//                     netBalance={selectedRow.netBalance}
//                   />
//                 </Panel>
//               </Grid>

//               <Grid item xs={12} md={6} xl={2.5}>
//                 <Panel
//                   title="Fluid Out"
//                   right={<SouthRoundedIcon sx={{ color: Colors.text.icon }} />}
//                   sx={{ background: alpha("#D9B63D", 0.14) }}
//                 >
//                   <Stack spacing={1} alignItems="center" sx={{ textAlign: "center" }}>
//                     <AnimatedUrineBag
//                       value={selectedRow.fluidOut}
//                       active={selectedAnimation.urineRunning}
//                     />

//                     <Typography sx={{ fontSize: 15, fontWeight: 900, color: Colors.text.title }}>
//                       OUTPUT
//                     </Typography>

//                     <Typography sx={{ fontSize: { xs: 34, md: 46 }, fontWeight: 900, lineHeight: 1, color: Colors.text.title }}>
//                       {fmt(selectedRow.fluidOut)}
//                     </Typography>

//                     <Typography sx={{ fontSize: 15, fontWeight: 800, color: Colors.text.body }}>
//                       ml
//                     </Typography>
//                   </Stack>
//                 </Panel>
//               </Grid>

//               <Grid item xs={12} lg={7.5}>
//                 <Panel
//                   title="Live Fluid Balance"
//                   right={<TrendingUpRoundedIcon sx={{ color: Colors.text.icon }} />}
//                 >
//                   <Stack spacing={1.5}>
//                     <Grid container spacing={1.2}>
//                       <Grid item xs={4}>
//                         <InfoLabel label="Input" value={`${fmt(selectedRow.fluidIn)} ml`} />
//                       </Grid>
//                       <Grid item xs={4}>
//                         <InfoLabel label="Output" value={`${fmt(selectedRow.fluidOut)} ml`} />
//                       </Grid>
//                       <Grid item xs={4}>
//                         <InfoLabel
//                           label="Net"
//                           value={`${selectedRow.netBalance > 0 ? "+" : ""}${fmt(selectedRow.netBalance)} ml`}
//                         />
//                       </Grid>
//                     </Grid>

//                     <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 700 }}>
//                       Balance Progress
//                     </Typography>

//                     <LinearProgress
//                       variant="determinate"
//                       value={Math.max(
//                         5,
//                         Math.min(
//                           100,
//                           ((Number(selectedRow.fluidIn) || 0) /
//                             Math.max(Number(selectedRow.totalVolume) || 1, 1)) *
//                             100
//                         )
//                       )}
//                       sx={{
//                         height: 12,
//                         borderRadius: 999,
//                         bgcolor: alpha(Colors.brand.primary, 0.12),
//                         "& .MuiLinearProgress-bar": {
//                           borderRadius: 999,
//                           background: Colors.brand.primary,
//                         },
//                       }}
//                     />
//                   </Stack>
//                 </Panel>
//               </Grid>

//               <Grid item xs={12} lg={4.5}>
//                 <AlarmPanel alarm={selectedRow.alarm} />
//               </Grid>

//               <Grid item xs={12}>
//                 <Panel title="Session Overview">
//                   <Grid container spacing={1.5}>
//                     <Grid item xs={6} md={3}>
//                       <InfoLabel label="Admission No" value={selectedRow.admissionNumber} />
//                     </Grid>
//                     <Grid item xs={6} md={3}>
//                       <InfoLabel label="Device Serial" value={selectedRow.deviceSerial} />
//                     </Grid>
//                     <Grid item xs={6} md={3}>
//                       <InfoLabel label="Bed" value={selectedRow.bedNumber} />
//                     </Grid>
//                     <Grid item xs={6} md={3}>
//                       <InfoLabel label="Assigned At" value={selectedRow.assignedAt} />
//                     </Grid>
//                   </Grid>
//                 </Panel>
//               </Grid>

//               <Grid item xs={12}>
//                 <FluidTherapyInfoCard row={selectedRow} />
//               </Grid>
//             </Grid>
//            <Box>
//          <FluidDistributionGraph/>
//       </Box>
//           </>
//         )}
//       </Stack>
      
      
      
//     </Box>
//   );
// }